"use strict";
const options = {
    collectionsLoadMore: '.collections-load-more',
    blsLinkFilter: '.bls__link-filter',
    openFilter: '.filter-title .open-children-toggle',
    rangeSlider: '.price-range .range-slider',
    rangeSliderMin: '.range-slider-min',
    rangeSliderMax: '.range-slider-max',
    gridMode: '.grid-mode-show-type-products .grid-mode',
    viewMode: '.view-mode',
    sidebarClose: '.sidebar-close',
    collectionSidebar: '#CollectionSidebar',
    collectionFiltersForm: '#CollectionFiltersForm',
    btnFilter: '.collection-filter .btn-filter',
    section: '.section-collection-product',
    toolbarSorter: '.toolbar-sorter .select-selected',
    facetFiltersSort: '.facet-filters__sort li',
    overlay: '.bls__overlay',
    filterSortMb: '.toolbar-sorter-mb .facet-filters__label',
    dataSortMb: '.toolbar-sorter-mb__option',
    closeSortMb: '.close-select'
};

let invalidateCollectionRouteLocation = function() {};
const mutateCollectionHistory = function(method, state, title, url) {
    invalidateCollectionRouteLocation();
    window.history[method](state, title, url);
};

var BlsEventCollectionShopify = (function() {
    let isReplayInProgress = false; // Guard against double replay
    let routeController = null;
    let sectionController = null;
    let sectionRoot = null;
    let sectionGeneration = 0;
    let filterRequest = null;
    let requestGeneration = 0;
    let replayController = null;
    let lazyObserver = null;
    let replayRestoreTimer = null;
    let scrollRestoreTimer = null;
    let scrollRestoreChain = null;
    let locationRestoreEpoch = 0;
    let sidebarHydration = null;
    const initializedSwatchColors = new WeakSet();
    const initializedSwatchActions = new WeakSet();
    const initializedQuickviews = new WeakSet();
    const initializedWishlists = new WeakSet();
    const initializedCompares = new WeakSet();
    let swatchTooltipTimer = null;

    function listen(target, type, handler, signal, options) {
        if (!target) return;
        const listenerOptions = Object.assign({}, options || {}, { signal: signal });
        target.addEventListener(type, handler, listenerOptions);
    }

    function scopedElements(scope, selector) {
        if (!scope) return [];
        const roots = typeof scope.length === 'number' && !scope.querySelectorAll ? Array.from(scope) : [scope];
        const elements = [];
        const seen = new Set();
        roots.forEach(root => {
            if (!root) return;
            if (root.matches && root.matches(selector) && !seen.has(root)) {
                seen.add(root);
                elements.push(root);
            }
            if (!root.querySelectorAll) return;
            root.querySelectorAll(selector).forEach(element => {
                if (seen.has(element)) return;
                seen.add(element);
                elements.push(element);
            });
        });
        return elements;
    }

    function hydrateViewportImage(image) {
        if (!image || !image.dataset.hobViewportSrc) return;
        image.srcset = image.dataset.hobViewportSrcset;
        image.src = image.dataset.hobViewportSrc;
        image.removeAttribute('data-hob-viewport-srcset');
        image.removeAttribute('data-hob-viewport-src');
    }

    function currentSectionMatches(root, generation) {
        return !!(
            root &&
            root === sectionRoot &&
            generation === sectionGeneration &&
            sectionController &&
            !sectionController.signal.aborted &&
            document.querySelector(options.section) === root
        );
    }

    function currentReplayMatches(replay, requireButton) {
        if (!replay || replayController !== replay.controller || replay.controller.signal.aborted) return false;
        if (!currentSectionMatches(replay.root, replay.sectionGeneration)) return false;
        return requireButton === false || document.querySelector(options.collectionsLoadMore) === replay.button;
    }

    function normalizedCollectionUrl(url) {
        if (typeof url !== 'string' || !url) return '';
        const withoutHash = url.split('#')[0];
        const queryIndex = withoutHash.indexOf('?');
        const pathname = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
        const params = new URLSearchParams(queryIndex === -1 ? '' : withoutHash.slice(queryIndex + 1));
        if (typeof params.sort === 'function') params.sort();
        const query = params.toString();
        return pathname + (query ? `?${query}` : '');
    }

    function normalizedLocation() {
        return {
            url: normalizedCollectionUrl(window.location.pathname + (window.location.search || '')),
            hash: window.location.hash || ''
        };
    }

    function clearScrollRestoreTimers() {
        clearTimeout(replayRestoreTimer);
        clearTimeout(scrollRestoreTimer);
        replayRestoreTimer = null;
        scrollRestoreTimer = null;
        scrollRestoreChain = null;
    }

    function invalidateScrollRestoreOwnership() {
        locationRestoreEpoch += 1;
        clearScrollRestoreTimers();
    }

    invalidateCollectionRouteLocation = invalidateScrollRestoreOwnership;

    function currentScrollRestoreOwnerMatches(restore) {
        if (!restore || scrollRestoreChain !== restore) return false;
        if (restore.locationEpoch !== locationRestoreEpoch) return false;
        if (
            !restore.controller ||
            restore.controller !== sectionController ||
            restore.controller.signal.aborted
        ) return false;
        if (!currentSectionMatches(restore.root, restore.sectionGeneration)) return false;
        return sessionStorage.getItem('collectionReturnState') === restore.savedState;
    }

    function currentScrollRestoreMatches(restore) {
        if (!currentScrollRestoreOwnerMatches(restore)) return false;
        const currentLocation = normalizedLocation();
        if (currentLocation.url !== restore.url || currentLocation.hash !== restore.hash) return false;
        if (currentLocation.url !== restore.savedUrl || currentLocation.hash !== restore.savedHash) return false;
        return true;
    }

    function releaseScrollRestore(restore) {
        if (scrollRestoreChain === restore) scrollRestoreChain = null;
    }

    return {
        init: function() {
            if (!routeController) {
                routeController = new AbortController();
                this.setupRouteListeners(routeController.signal);
            }

            this.mount(document.querySelector(options.section));
        },

        mount: function(root) {
            if (!root || (root === sectionRoot && sectionController && !sectionController.signal.aborted)) return;
            this.disposeSection();
            sectionRoot = root;
            sectionGeneration += 1;
            sectionController = new AbortController();
            const signal = sectionController.signal;

            this.setupEventListeners(signal);
            this.eventLoadMore(signal);
            this.ionRangeSlider(signal);
            this.actionDesc(signal);
            this.setupDeferredHoverImages(root, signal);
            this.activateLazyImages(root);
            this.replayPagesFromHash();
        },

        disposeSection: function(settings) {
            const preservedFilterRequest = settings && settings.preserveFilterRequest;
            if (
                filterRequest &&
                filterRequest !== preservedFilterRequest &&
                filterRequest.root === sectionRoot &&
                filterRequest.sectionGeneration === sectionGeneration
            ) {
                filterRequest.controller.abort();
                filterRequest = null;
                this.toggleLoading(document.body, false);
            }
            if (sectionController) sectionController.abort();
            sidebarHydration = null;
            if (lazyObserver) lazyObserver.disconnect();
            lazyObserver = null;
            clearTimeout(swatchTooltipTimer);
            swatchTooltipTimer = null;
            clearScrollRestoreTimers();
            if (replayController) replayController.abort();
            replayController = null;
            isReplayInProgress = false;
            sectionController = null;
            sectionRoot = null;
            sectionGeneration += 1;
        },

        setupRouteListeners: function(signal) {
            listen(window, 'pageshow', () => {
                invalidateScrollRestoreOwnership();
                if (replayController) replayController.abort();
                replayController = null;
                isReplayInProgress = false;
                this.replayPagesFromHash();
            }, signal);

            listen(window, 'popstate', () => {
                invalidateScrollRestoreOwnership();
                const searchParams = window.location.search.replace(/^\?/, '');
                this.renderSectionFilter(this.renderUrl(searchParams), false, { updateHistory: false });
            }, signal);

            listen(window, 'hashchange', () => {
                invalidateScrollRestoreOwnership();
            }, signal);

            listen(document, 'shopify:section:load', event => {
                const target = event.target;
                const root = target && target.matches && target.matches(options.section)
                    ? target
                    : target && target.querySelector
                        ? target.querySelector(options.section)
                        : null;
                if (root) {
                    this.mount(root);
                    this.initializeProductIntegrations(root);
                }
            }, signal);

            listen(document, 'shopify:section:unload', event => {
                const target = event.target;
                if (!sectionRoot || !target) return;
                if (target === sectionRoot || (target.contains && target.contains(sectionRoot))) {
                    this.disposeSection();
                }
            }, signal);

            listen(document, 'click', (e) => {
                const productLink = e.target.closest('#bls__product-grid a[href*="/products/"]');
                if (!productLink) return;
                const state = {
                    collectionUrl: window.location.pathname + window.location.search,
                    collectionHash: window.location.hash,
                    scrollY: window.scrollY,
                    productHandle: productLink.getAttribute('href').split('/products/')[1]?.split('?')[0]
                };
                sessionStorage.setItem('collectionReturnState', JSON.stringify(state));
            }, signal);

            listen(document.body, 'click', (evt) => {
                const target = evt.target;
                const btn_dropdonw_sidebar = document.querySelector('.collection-filter.dropdonw_sidebar .btn-filter');
                const collectionSidebar = document.querySelector(options.collectionSidebar);
                const isCollectionSidebarOpen = collectionSidebar && collectionSidebar.classList.contains('bls__opend-popup');
                const isOverlayClick = target.classList.contains('bls__overlay') || target.closest('.bls__overlay');

                if (!target.closest('.select-custom') && target != document.querySelector('.toolbar-sorter .select-custom')) {
                    const sorter = document.querySelector('.toolbar-sorter .select-custom');
                    if (sorter) sorter.classList.remove('actived');
                }

                if (isOverlayClick && isCollectionSidebarOpen) {
                    collectionSidebar.classList.remove('bls__opend-popup');
                    const btnFilter = document.querySelector('.collection-filter .btn-filter');
                    if (btnFilter) btnFilter.classList.remove('actived');
                    const overlay = document.querySelector(options.overlay);
                    if (overlay) overlay.classList.add('d-none-overlay');
                    document.documentElement.classList.remove('hside_opened');
                    if (btn_dropdonw_sidebar && window.innerWidth >= 1024) {
                        slideAnime({
                            target: btn_dropdonw_sidebar.closest('#productgridcontainer').querySelector('#CollectionSidebar'),
                            animeType: 'slideUp'
                        });
                    }
                    return;
                }

                if (!target.closest('#CollectionSidebar') && target != collectionSidebar && btn_dropdonw_sidebar && target != btn_dropdonw_sidebar && window.innerWidth >= 1024) {
                    btn_dropdonw_sidebar.classList.remove('actived');
                    if (collectionSidebar) collectionSidebar.classList.remove('bls__opend-popup');
                    const overlay = document.querySelector(options.overlay);
                    if (overlay) overlay.classList.add('d-none-overlay');
                    document.documentElement.classList.remove('hside_opened');
                    slideAnime({
                        target: btn_dropdonw_sidebar.closest('#productgridcontainer').querySelector('#CollectionSidebar'),
                        animeType: 'slideUp'
                    });
                }
            }, signal);
        },

        actionDesc: function(signal){
            const viewMore = document.querySelector('[data-view-more]');
            if (viewMore) {
                listen(viewMore, 'click', function(e) {
                    const target = e.currentTarget;
                    if (target.closest('.heading-description').classList.contains('show-full-desc')) {
                        target.closest('.heading-description').classList.remove('show-full-desc');
                        target.textContent = target.dataset.readMore
                    } else {
                        target.closest('.heading-description').classList.add('show-full-desc');
                        target.textContent = target.dataset.readLess
                    }
                }, signal);
            }
        },

        hydrateFilterSidebar: function(signal) {
            const shell = document.querySelector(`${options.collectionSidebar}[data-hob-filter-shell]`);
            if (!shell) return Promise.resolve(document.querySelector(options.collectionSidebar));
            if (sidebarHydration) return sidebarHydration.promise;

            const sectionId = shell.dataset.sectionId;
            const params = new URLSearchParams(window.location.search);
            params.set('section_id', sectionId);
            params.set('hob_filter_fetch', '1');
            const requestUrl = `${window.location.pathname}?${params.toString()}`;
            const request = {};
            request.promise = fetch(requestUrl, { signal: signal })
                .then(response => {
                    if (!response.ok) throw new Error(response.status);
                    return response.text();
                })
                .then(responseText => {
                    const parsed = new DOMParser().parseFromString(responseText, 'text/html');
                    const hydratedSidebar = parsed.querySelector(options.collectionSidebar);
                    const currentShell = document.querySelector(`${options.collectionSidebar}[data-hob-filter-shell]`);
                    if (!hydratedSidebar || !currentShell) throw new Error('Collection filter sidebar not found in response');
                    currentShell.replaceWith(hydratedSidebar);
                    this.bindSidebarEvents(hydratedSidebar, signal);
                    this.ionRangeSlider(signal);
                    return hydratedSidebar;
                })
                .catch(error => {
                    if (error.name !== 'AbortError') console.error('Collection filter hydration error:', error);
                    return null;
                })
                .finally(() => {
                    if (sidebarHydration === request) sidebarHydration = null;
                });
            sidebarHydration = request;
            return request.promise;
        },

        toggleFilterSidebar: function(target, collectionSidebar) {
            if (!collectionSidebar) return;
            const overlay = document.querySelector(options.overlay);
            const collectionFilter = target.closest('.collection-filter');
            const isDropdownSidebar = collectionFilter && collectionFilter.classList.contains('dropdonw_sidebar');

            if (collectionSidebar.classList.contains('bls__opend-popup')) {
                target.classList.remove('actived');
                collectionSidebar.classList.remove('bls__opend-popup');
                if (overlay) overlay.classList.add('d-none-overlay');
                document.documentElement.classList.remove('hside_opened');
                if (isDropdownSidebar && window.innerWidth >= 1024) {
                    slideAnime({
                        target: target.closest('#productgridcontainer').querySelector(options.collectionSidebar),
                        animeType: 'slideToggle'
                    });
                }
                return;
            }

            target.classList.add('actived');
            collectionSidebar.classList.add('bls__opend-popup');
            const isFilterCanvas = collectionFilter && collectionFilter.classList.contains('filter_canvas');
            if (isFilterCanvas || (!isDropdownSidebar && window.innerWidth < 1024)) {
                document.documentElement.classList.add('hside_opened');
                if (overlay) overlay.classList.remove('d-none-overlay');
            } else if (isDropdownSidebar) {
                if (window.innerWidth < 1024) {
                    document.documentElement.classList.add('hside_opened');
                    if (overlay) overlay.classList.remove('d-none-overlay');
                } else {
                    slideAnime({
                        target: target.closest('#productgridcontainer').querySelector(options.collectionSidebar),
                        animeType: 'slideToggle'
                    });
                }
            }
        },

        bindSidebarEvents: function(sidebar, signal) {
            if (!sidebar) return;
            sidebar.querySelectorAll(options.openFilter).forEach(btn => {
                listen(btn, 'click', event => {
                    event.preventDefault();
                    const target = event.currentTarget;
                    slideAnime({
                        target: target.closest('.filter-item').querySelector('.filter-content'),
                        animeType: 'slideToggle'
                    });
                    target.classList.toggle('active');
                }, signal);
            });

            sidebar.querySelectorAll(options.sidebarClose).forEach(btn => {
                listen(btn, 'click', event => {
                    event.preventDefault();
                    sidebar.classList.remove('bls__opend-popup');
                    const filterButton = document.querySelector(options.btnFilter);
                    if (filterButton) filterButton.classList.remove('actived');
                    const overlay = document.querySelector(options.overlay);
                    if (overlay) overlay.classList.add('d-none-overlay');
                    document.documentElement.classList.remove('hside_opened');
                }, signal);
            });

            sidebar.querySelectorAll(options.blsLinkFilter).forEach(btn => {
                listen(btn, 'click', event => {
                    event.preventDefault();
                    const target = event.currentTarget;
                    const tooltip = target.querySelector('.bls_tooltip');
                    if (tooltip) tooltip.classList.toggle('current-filter');
                    else if (target.closest('li')) target.closest('li').classList.toggle('current-filter');
                    this.renderSectionFilter(target.getAttribute('href'));
                }, signal);
            });
        },

        setupEventListeners: function(signal) {
            document.querySelectorAll(options.btnFilter).forEach(btn => {
                listen(btn, "click", event => {
                    event.preventDefault();
                    const target = event.currentTarget;
                    if (target.getAttribute('aria-busy') === 'true') return;
                    const sidebar = document.querySelector(options.collectionSidebar);
                    if (!sidebar || !sidebar.hasAttribute('data-hob-filter-shell')) {
                        this.toggleFilterSidebar(target, sidebar);
                        return;
                    }
                    target.setAttribute('aria-busy', 'true');
                    this.hydrateFilterSidebar(signal).then(hydratedSidebar => {
                        if (hydratedSidebar) this.toggleFilterSidebar(target, hydratedSidebar);
                    }).finally(() => target.removeAttribute('aria-busy'));
                }, signal);
            });
            this.bindSidebarEvents(document.querySelector(options.collectionSidebar), signal);

            document.querySelectorAll(options.viewMode).forEach(view => {
                listen(view, "click", event => {
                    event.preventDefault();
                    const target = event.currentTarget;
                    const view_mode = target.getAttribute('data-view');
                    if (target.classList.contains('actived')) return;
                    var queryString = window.location.search;
                    queryString = this.removeParam('view', queryString);
                    if (view_mode == 'list') {
                        var searchParams = queryString.replace('?', '')+'&view=list';
                    } else {
                        var searchParams = queryString.replace('?', '')+'&view=grid';
                    }
                    const url = this.renderUrl(searchParams);
                    this.renderSectionFilter(url, searchParams);
                }, signal);
            });

            document.querySelectorAll(options.gridMode).forEach(btn => {
                listen(btn, "click", event => {
                    event.preventDefault();
                    const target = event.currentTarget;
                    const view_mode = target.getAttribute('data-grid-mode');
                    if (target.classList.contains('actived')) return;
                    if (view_mode == 1) return;
                    for (var item of document.querySelectorAll(options.gridMode)) {
                        item.classList.remove('actived');
                    }
                    const container_switch = document.querySelector('.container-products-switch');
                    target.classList.add('actived');
                    const data_view_mode = container_switch.getAttribute('data-view-mode');
                    container_switch.classList.remove('grid-columns-'+data_view_mode);
                    container_switch.setAttribute('data-view-mode',view_mode);
                    container_switch.classList.add('grid-columns-'+view_mode);
                }, signal);
            });

            document.querySelectorAll(options.blsLinkFilter).forEach(btn => {
                if (btn.closest(options.collectionSidebar)) return;
                listen(btn, "click", event => {
                    event.preventDefault();
                    const target = event.currentTarget;
                    if (target.querySelector('.bls_tooltip')) {
                        target.querySelector('.bls_tooltip').classList.toggle('current-filter');
                    } else {
                        target.closest('li').classList.toggle('current-filter');
                    }
                    const url = target.getAttribute('href');
                    this.renderSectionFilter(url);
                }, signal);
            });

            document.querySelectorAll(options.toolbarSorter).forEach(filterSort => {
                listen(filterSort, "click", event => {
                    event.preventDefault();
                    const target = event.currentTarget;
                    if (target.closest('.select-custom').classList.contains('actived')) {
                        target.closest('.select-custom').classList.remove('actived');
                    } else {
                        target.closest('.select-custom').classList.add('actived');
                    }
                }, signal);
            });

            if (document.querySelector(options.filterSortMb)) {
                listen(document.querySelector(options.filterSortMb), 'click' , event => {
                    event.preventDefault();
                    const target = event.currentTarget;
                    if (target.closest('.toolbar-sorter-mb').classList.contains('open-selected')) {
                        target.closest('.toolbar-sorter-mb').classList.remove('open-selected')
                    } else {
                        target.closest('.toolbar-sorter-mb').classList.add('open-selected')
                    }
                }, signal);
            }

            if (document.querySelector(options.closeSortMb)) {
                listen(document.querySelector(options.closeSortMb), 'click', event => {
                    event.preventDefault();
                    document.querySelector('.toolbar-sorter-mb').classList.remove('open-selected')
                }, signal)
            }

            document.querySelectorAll(options.facetFiltersSort).forEach(filterSort => {
                listen(filterSort, "click", event => {
                    event.preventDefault();
                    const target = event.currentTarget;
                    const value = target.getAttribute('value');
                    var queryString = window.location.search;
                    queryString = this.removeParam('sort_by', queryString);
                    var searchParams = queryString.replace('?', '')+'&sort_by='+value;
                    const url = this.renderUrl(searchParams);
                    this.renderSectionFilter(url, searchParams);
                }, signal);
            });
        },

        ionRangeSlider: function(signal) {
            // Global function to apply ALL sliders with visible buttons
            const applyAllSliders = function() {
                const currentParams = new URLSearchParams(window.location.search);
                let hasChanges = false;

                // Apply price slider if button is visible
                const priceForm = document.querySelector('#CollectionFiltersForm');
                const priceApplyButton = priceForm ? priceForm.querySelector('.price-slider-apply-btn') : null;
                if (priceApplyButton && priceApplyButton.style.display !== 'none') {
                    const minPrice = priceForm.querySelector('.min-price-range');
                    const maxPrice = priceForm.querySelector('.max-price-range');
                    if (minPrice && maxPrice) {
                        const nameMin = minPrice.getAttribute('name');
                        const nameMax = maxPrice.getAttribute('name');
                        // Remove existing params
                        const keysToDelete = [];
                        for (const [key, value] of currentParams.entries()) {
                            if (key === nameMin || key === nameMax) {
                                keysToDelete.push(key);
                            }
                        }
                        keysToDelete.forEach(function(key) {
                            currentParams.delete(key);
                        });
                        // Add new params
                        if (minPrice.value) currentParams.set(nameMin, minPrice.value);
                        if (maxPrice.value) currentParams.set(nameMax, maxPrice.value);
                        hasChanges = true;
                        priceApplyButton.style.display = 'none';
                    }
                }

                // Apply all numeric sliders with visible buttons
                document.querySelectorAll('.filter-numeric-slider').forEach(function(sliderContainer) {
                    const applyButton = sliderContainer.querySelector('.numeric-slider-apply-btn');
                    if (applyButton && applyButton.style.display !== 'none') {
                        const minRange = sliderContainer.querySelector('.numeric-range-slider-min');
                        const maxRange = sliderContainer.querySelector('.numeric-range-slider-max');
                        const filterParam = sliderContainer.dataset.filterParam;
                        const filterValuesStr = sliderContainer.dataset.filterValues || '';

                        if (minRange && maxRange && filterParam && filterValuesStr) {
                            const minVal = parseFloat(minRange.value);
                            const maxVal = parseFloat(maxRange.value);

                            // Parse filter values
                            const filterValues = filterValuesStr.split('|').map(function(v) {
                                const parts = v.trim().split(':');
                                return {
                                    numeric: parseFloat(parts[0]) || 0,
                                    value: parts[0] || ''
                                };
                            }).filter(function(v) { return v.numeric > 0; });

                            // Remove existing params for this filter
                            const keysToDelete = [];
                            for (const [key, value] of currentParams.entries()) {
                                if (key === filterParam) {
                                    keysToDelete.push(key);
                                }
                            }
                            keysToDelete.forEach(function(key) {
                                currentParams.delete(key);
                            });

                            // Add ALL values within the selected range
                            const valuesInRange = filterValues.filter(function(v) {
                                return v.numeric >= minVal && v.numeric <= maxVal;
                            });

                            if (valuesInRange.length > 0) {
                                valuesInRange.forEach(function(v) {
                                    currentParams.append(filterParam, v.value);
                                });
                                hasChanges = true;
                                applyButton.style.display = 'none';
                            }
                        }
                    }
                });

                // Apply all changes at once
                if (hasChanges) {
                    const searchParams = currentParams.toString();
                    const url = this.renderUrl(searchParams);
                    this.renderSectionFilter(url, searchParams);
                }
            }.bind(this);

            // Handle price range slider (existing) - only for price filters
            const priceForm = document.querySelector('#CollectionFiltersForm');
            if (priceForm) {
                const price_range = priceForm.querySelector('.price-range');
                const maxRange = priceForm.querySelector(options.rangeSliderMax);
                const minRange = priceForm.querySelector(options.rangeSliderMin);
                const minPrice = priceForm.querySelector('.min-price-range');
                const maxPrice = priceForm.querySelector('.max-price-range');
                const priceApplyButton = priceForm.querySelector('.price-slider-apply-btn');
                var minPriceRange = 0;
                var maxPriceRange = 0;

                if (price_range && maxRange && minRange && minPrice && maxPrice) {
                    // Store initial values
                    let initialMin = minPrice.value ? Number(minPrice.value) : 0;
                    let initialMax = maxPrice.value ? Number(maxPrice.value) : Number(maxRange.getAttribute('max'));

                    // Function to check if price values have changed
                    const checkPriceChanges = function() {
                        const currentMin = minPrice.value ? Number(minPrice.value) : 0;
                        const currentMax = maxPrice.value ? Number(maxPrice.value) : Number(maxRange.getAttribute('max'));
                        const hasChanged = (currentMin !== initialMin || currentMax !== initialMax);

                        if (priceApplyButton) {
                            if (hasChanged) {
                                priceApplyButton.style.display = 'inline-flex';
                            } else {
                                priceApplyButton.style.display = 'none';
                            }
                        }
                    };

                    listen(minRange, "input", event => {
                        event.preventDefault();
                        const target = event.currentTarget;
                        if (Number(maxRange.value) - Number(target.value) >= 1) {
                            minPrice.value = Number(target.value);
                            minPriceRange = Number(target.value) * 100;
                        } else {
                            target.value = Number(maxRange.value) - 1;
                            minPriceRange = (Number(maxRange.value) - 1)*100;
                        }
                        const price_format = Shopify.formatMoney(minPriceRange, cartStrings.money_format);
                        const priceLabel = target.closest('.filter-item').querySelector('.price-lable .min');
                        if (priceLabel) priceLabel.innerHTML = price_format;
                        target.closest(".price-range").style.setProperty('--range-from', minRange.value / Number(target.getAttribute('max')) * 100 + '%');
                        checkPriceChanges();
                    }, signal);

                    listen(maxRange, "input", event => {
                        event.preventDefault();
                        const target = event.currentTarget;
                        if (target.value - minRange.value >= 1) {
                            maxPrice.value = target.value;
                            maxPriceRange = Number(target.value) * 100;
                        } else {
                            target.value = Number(minRange.value) + Number(1);
                            maxPriceRange = (Number(minRange.value) + Number(1)) * 100;
                        }
                        const price_format = Shopify.formatMoney(maxPriceRange, cartStrings.money_format);
                        const priceLabel = target.closest('.filter-item').querySelector('.price-lable .max');
                        if (priceLabel) priceLabel.innerHTML = price_format;
                        target.closest(".price-range").style.setProperty('--range-to', maxRange.value / Number(target.getAttribute('max')) * 100 + '%');
                        checkPriceChanges();
                    }, signal);

                    // Remove automatic change event - now handled by button
                    // Add click event to price apply button
                    if (priceApplyButton) {
                        listen(priceApplyButton, "click", function(event) {
                            event.preventDefault();
                            applyAllSliders();
                        }, signal);
                    }
                }
            }

            // Handle numeric filter sliders (new) - activate ALL values in range at once
            document.querySelectorAll('.filter-numeric-slider').forEach(function(sliderContainer) {
                const numericRange = sliderContainer.querySelector('.numeric-range');
                const minRange = sliderContainer.querySelector('.numeric-range-slider-min');
                const maxRange = sliderContainer.querySelector('.numeric-range-slider-max');
                const minLabel = sliderContainer.querySelector('.numeric-range-label .min');
                const maxLabel = sliderContainer.querySelector('.numeric-range-label .max');
                const unitSpan = sliderContainer.querySelector('.numeric-range-label .unit');
                const applyButton = sliderContainer.querySelector('.numeric-slider-apply-btn');
                const filterParam = sliderContainer.dataset.filterParam;
                const filterValuesStr = sliderContainer.dataset.filterValues || '';
                const isMeasurement = sliderContainer.dataset.isMeasurement === 'true';

                // Parse filter values: format is "numeric:url_to_add:active|numeric:url_to_add:active"
                const filterValues = filterValuesStr ? filterValuesStr.split('|').map(function(v) {
                    const parts = v.trim().split(':');
                    return {
                        numeric: parseFloat(parts[0]) || 0,
                        value: parts[0] || '',  // Keep original string format
                        url: parts[1] || '',
                        active: parts[2] === '1'
                    };
                }).filter(function(v) { return v.numeric > 0; }) : [];

                const rangeMin = numericRange ? parseFloat(numericRange.dataset.rangeMin || 0) : 0;
                const rangeMax = numericRange ? parseFloat(numericRange.dataset.rangeMax || 100) : 100;

                // Helper function to format number (remove decimal if whole number)
                const formatNumber = function(num) {
                    const rounded = Math.round(num);
                    return num === rounded ? rounded : num;
                };

                if (minRange && maxRange && filterValues.length > 0 && filterParam) {
                    // Store initial/active values to compare later
                    let initialMin = parseFloat(minRange.value);
                    let initialMax = parseFloat(maxRange.value);

                    // Check if there are active filters and set slider positions accordingly
                    const activeValues = filterValues.filter(function(v) { return v.active; });
                    if (activeValues.length > 0) {
                        const activeMin = Math.min.apply(Math, activeValues.map(function(v) { return v.numeric; }));
                        const activeMax = Math.max.apply(Math, activeValues.map(function(v) { return v.numeric; }));

                        // Set slider positions to active values
                        minRange.value = activeMin;
                        maxRange.value = activeMax;

                        // Store as initial values
                        initialMin = activeMin;
                        initialMax = activeMax;

                        // Update labels
                        if (minLabel) minLabel.textContent = formatNumber(activeMin);
                        if (maxLabel) maxLabel.textContent = formatNumber(activeMax);

                        // Update visual range bar
                        const rangeFrom = rangeMax > rangeMin ? ((activeMin - rangeMin) / (rangeMax - rangeMin)) * 100 : 0;
                        const rangeTo = rangeMax > rangeMin ? ((activeMax - rangeMin) / (rangeMax - rangeMin)) * 100 : 100;
                        if (numericRange) {
                            numericRange.style.setProperty('--range-from', rangeFrom + '%');
                            numericRange.style.setProperty('--range-to', rangeTo + '%');
                        }
                    }

                    // Function to check if values have changed and show/hide apply button
                    const checkForChanges = function() {
                        const currentMin = parseFloat(minRange.value);
                        const currentMax = parseFloat(maxRange.value);
                        const hasChanged = (currentMin !== initialMin || currentMax !== initialMax);

                        if (applyButton) {
                            if (hasChanged) {
                                applyButton.style.display = 'inline-flex';
                            } else {
                                applyButton.style.display = 'none';
                            }
                        }
                    };

                    // Update visual feedback on slider input (no page reload)
                    listen(minRange, "input", function(event) {
                        const target = event.currentTarget;
                        const maxVal = parseFloat(maxRange.value);
                        const minVal = parseFloat(target.value);

                        if (maxVal - minVal >= 0) {
                            if (minLabel) minLabel.textContent = formatNumber(minVal);
                            const rangeFrom = rangeMax > rangeMin ? ((minVal - rangeMin) / (rangeMax - rangeMin)) * 100 : 0;
                            if (numericRange) numericRange.style.setProperty('--range-from', rangeFrom + '%');
                        } else {
                            target.value = maxVal;
                            if (minLabel) minLabel.textContent = formatNumber(maxVal);
                        }

                        // Check if values changed and show apply button
                        checkForChanges();
                    }, signal);

                    listen(maxRange, "input", function(event) {
                        const target = event.currentTarget;
                        const minVal = parseFloat(minRange.value);
                        const maxVal = parseFloat(target.value);

                        if (maxVal - minVal >= 0) {
                            if (maxLabel) maxLabel.textContent = formatNumber(maxVal);
                            const rangeTo = rangeMax > rangeMin ? ((maxVal - rangeMin) / (rangeMax - rangeMin)) * 100 : 100;
                            if (numericRange) numericRange.style.setProperty('--range-to', rangeTo + '%');
                        } else {
                            target.value = minVal;
                            if (maxLabel) maxLabel.textContent = formatNumber(minVal);
                        }

                        // Check if values changed and show apply button
                        checkForChanges();
                    }, signal);

                    // Update initial values when slider is applied (called from applyAllSliders)
                    // This is handled by the global applyAllSliders function

                    // Add click event to apply button - calls global function
                    if (applyButton) {
                        listen(applyButton, "click", function(event) {
                            event.preventDefault();
                            // Update initial values for this slider
                            initialMin = parseFloat(minRange.value);
                            initialMax = parseFloat(maxRange.value);
                            // Apply all sliders
                            applyAllSliders();
                        }, signal);
                    }
                }
            }.bind(this));
        },

        removeParam: function(key, sourceURL) {
            var rtn = sourceURL.split("?")[0],
                param,
                params_arr = [],
                queryString = (sourceURL.indexOf("?") !== -1) ? sourceURL.split("?")[1] : "";
            if (queryString !== "") {
                params_arr = queryString.split("&");
                for (var i = params_arr.length - 1; i >= 0; i -= 1) {
                    param = params_arr[i].split("=")[0];
                    if (param === key) {
                        params_arr.splice(i, 1);
                    }
                }
                if (params_arr.length) rtn = rtn + "?" + params_arr.join("&");
            }
            return rtn;
        },

        renderUrl: function(searchParams) {
            const sectionId = document.querySelector(options.section).dataset.sectionId;
            const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
            return url;
        },

        renderSectionFilter: function(url, searchParams, settings) {
            const shouldUpdateHistory = !settings || settings.updateHistory !== false;
            const generation = ++requestGeneration;
            clearScrollRestoreTimers();
            if (filterRequest) filterRequest.controller.abort();
            if (replayController) replayController.abort();
            replayController = null;
            isReplayInProgress = false;
            const request = {
                controller: new AbortController(),
                generation: generation,
                root: sectionRoot,
                sectionGeneration: sectionGeneration
            };
            filterRequest = request;
            const requestSignal = request.controller.signal;
            const oldSidebar = document.querySelector(options.collectionSidebar);
            const oldOverlay = document.querySelector(options.overlay);
            const preserveSidebarOpen = !!(oldSidebar && oldSidebar.classList.contains('bls__opend-popup'));
            const preserveOverlayVisible = !!(oldOverlay && !oldOverlay.classList.contains('d-none-overlay'));
            const preserveDocumentOpen = document.documentElement.classList.contains('hside_opened');
            let requestUrl = url;
            if (oldSidebar && oldSidebar.classList.contains('filter_canvas') && !oldSidebar.hasAttribute('data-hob-filter-shell')) {
                const filterUrl = new URL(url, window.location.origin);
                filterUrl.searchParams.set('hob_filter_fetch', '1');
                requestUrl = `${filterUrl.pathname}${filterUrl.search}${filterUrl.hash}`;
            }

            this.toggleLoading(document.body, true);

            return fetch(requestUrl, { signal: requestSignal })
                .then((response) => {
                    if (!response.ok) {
                    var error = new Error(response.status);
                    throw error;
                    }
                    return response.text();
                })
                .then(responseText => {
                    const newSection = new DOMParser().parseFromString(responseText, 'text/html').querySelector(options.section);
                    if (!newSection) throw new Error('Collection section not found in response');
                    if (
                        requestSignal.aborted ||
                        generation !== requestGeneration ||
                        request !== filterRequest ||
                        !currentSectionMatches(request.root, request.sectionGeneration)
                    ) return false;

                    const oldSection = document.querySelector(options.section);
                    if (!oldSection || oldSection !== request.root) return false;
                    this.disposeSection({ preserveFilterRequest: request });
                    if (shouldUpdateHistory) this.updateUrl(url, searchParams);
                    oldSection.replaceWith(newSection);

                    const collectionSidebar = document.querySelector(options.collectionSidebar);
                    const overlay = document.querySelector(options.overlay);
                    const filterButton = document.querySelector(options.btnFilter);
                    if (preserveSidebarOpen && collectionSidebar) {
                        collectionSidebar.classList.add('bls__opend-popup');
                        if (filterButton) filterButton.classList.add('actived');
                    } else {
                        if (filterButton) filterButton.classList.remove('actived');
                    }
                    document.documentElement.classList.toggle('hside_opened', preserveDocumentOpen);
                    if (overlay) overlay.classList.toggle('d-none-overlay', !preserveOverlayVisible);

                    this.mount(newSection);
                    this.initializeProductIntegrations(newSection);
                    return true;
                })
                .catch((error) => {
                    if (error.name !== 'AbortError') console.error('Collection filter error:', error);
                    return false;
                })
                .finally(() => {
                    if (request === filterRequest) {
                        filterRequest = null;
                        this.toggleLoading(document.body, false);
                    }
                });
        },

        eventLoadMore: function(signal) {
            document.querySelectorAll(options.collectionsLoadMore).forEach(loadMore => {
                var _this = this;
                if (loadMore.classList.contains('infinit-scrolling')) {
                    var observer = new IntersectionObserver(function (entries) {
                        entries.forEach(entry => {
                            if (entry.intersectionRatio === 1){
                                _this.loadMoreProducts(loadMore);
                            }
                        });
                    }, {threshold: 1.0});
                    observer.observe(loadMore);
                    signal.addEventListener('abort', function() {
                        observer.disconnect();
                    }, { once: true });
                } else {
                    listen(loadMore, "click", event => {
                        event.preventDefault();
                        const target = event.currentTarget;
                        _this.loadMoreProducts(target);
                    }, signal);
                }
            });
        },

        activateLazyImages: function(root) {
            if (!root || !window.IntersectionObserver || !sectionController) return;
            if (!lazyObserver) {
                lazyObserver = new IntersectionObserver((entries, imageObserver) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) return;
                        hydrateViewportImage(entry.target);
                        entry.target.width = entry.boundingClientRect.width;
                        entry.target.height = entry.boundingClientRect.height;
                        entry.target.sizes = `${entry.boundingClientRect.width}px`;
                        entry.target.classList.add('bls-loaded-image');
                        const wrapper = entry.target.closest('.bls-image-js');
                        if (wrapper) wrapper.classList.remove('bls-loading-image');
                        imageObserver.unobserve(entry.target);
                    });
                }, { rootMargin: '600px 0px' });
            }
            root.querySelectorAll('.bls-image-js img:not(.bls-loaded-image):not([data-hob-hover-src])').forEach((img) => lazyObserver.observe(img));
        },

        setupDeferredHoverImages: function(root, signal) {
            if (!root || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
            const settle = (image, ready) => {
                const hoverImage = image.closest('.bls__product-hover-img');
                if (!hoverImage) return;
                if (!ready) {
                    image.classList.remove('bls-loaded-image');
                    hoverImage.classList.remove('is-hover-ready');
                    return;
                }
                image.classList.add('bls-loaded-image');
                image.closest('.bls-image-js')?.classList.remove('bls-loading-image');
                hoverImage.classList.add('is-hover-ready');
            };
            const hydrate = event => {
                const productLink = event.target.closest('.bls__img-url');
                if (!productLink || !root.contains(productLink)) return;
                const image = productLink.querySelector('.bls__product-hover-img img[data-hob-hover-src]');
                if (!image) return;

                image.addEventListener('load', () => settle(image, true), { once: true, signal });
                image.addEventListener('error', () => settle(image, false), { once: true, signal });
                const primaryImage = productLink.querySelector('.bls__product-main-img img');
                if (primaryImage?.sizes) image.sizes = primaryImage.sizes;
                image.loading = 'eager';
                if (image.dataset.hobHoverSrcset) image.srcset = image.dataset.hobHoverSrcset;
                image.src = image.dataset.hobHoverSrc;
                image.removeAttribute('data-hob-hover-srcset');
                image.removeAttribute('data-hob-hover-src');
                if (image.complete) settle(image, image.naturalWidth > 0);
            };
            listen(root, 'pointerover', hydrate, signal);
            listen(root, 'focusin', hydrate, signal);
        },

        initializeProductIntegrations: function(scope) {
            if (!scope) return;

            const scopedActionApi = typeof BlsSubActionProduct !== 'undefined' ? BlsSubActionProduct : null;
            if (scopedActionApi && typeof scopedActionApi.activateProductScope === 'function') {
                scopedActionApi.activateProductScope(scope);
                return;
            }

            // Collection review badges are server-rendered. The legacy SPR adapter has
            // no scoped interface, so rerunning it here would rescan the whole document.
            const swatchApi = typeof BlsColorSwatchesShopify !== 'undefined' ? BlsColorSwatchesShopify : null;
            scopedElements(scope, '.bls__product-color-swatches').forEach(element => {
                if (initializedSwatchColors.has(element)) return;
                initializedSwatchColors.add(element);
                if (swatchApi && typeof swatchApi.checkSwatches === 'function') swatchApi.checkSwatches(element);
            });

            scopedElements(scope, '.bls__option-swatch-js,.bls__product-compare,.bls__product-wishlist').forEach(element => {
                if (initializedSwatchActions.has(element)) return;
                const productTarget = element.closest('.bls__product-item');
                if (!productTarget) return;
                initializedSwatchActions.add(element);
                element.addEventListener('mouseout', function() {
                    if (!element.closest('.swiper')) return;
                    swatchTooltipTimer = setTimeout(() => {
                        const swiper = element.closest('.swiper');
                        if (swiper) swiper.classList.remove('show-tooltip');
                    }, 400);
                });
                element.addEventListener('mouseover', function() {
                    clearTimeout(swatchTooltipTimer);
                    if (swatchApi && typeof swatchApi.listenerColor === 'function') {
                        swatchApi.listenerColor(element, productTarget);
                    }
                    const swiper = element.closest('.swiper');
                    if (swiper) swiper.classList.add('show-tooltip');
                });
            });

            const actionApi = typeof BlsSubActionProduct !== 'undefined' ? BlsSubActionProduct : null;
            scopedElements(scope, '.bls__product-quickview').forEach(element => {
                if (initializedQuickviews.has(element) || !actionApi || typeof actionApi.handleFetchDataQuickView !== 'function') return;
                initializedQuickviews.add(element);
                element.addEventListener('click', function() {
                    element.classList.add('btn-loading');
                    if (element.querySelectorAll('span.loader-icon').length === 0 && element.querySelectorAll('div.loader-icon').length === 0) {
                        const loader = document.createElement('div');
                        loader.classList.add('loader-icon');
                        element.appendChild(loader);
                    }
                    const productTarget = element.closest('.bls__product-item');
                    if (productTarget) actionApi.handleFetchDataQuickView(element, productTarget.dataset.productHandle);
                });
            });

            scopedElements(scope, '.bls__product-wishlist-js').forEach(element => {
                if (initializedWishlists.has(element) || !actionApi || typeof actionApi.handleWishlistFunctionClick !== 'function') return;
                initializedWishlists.add(element);
                element.addEventListener('click', actionApi.handleWishlistFunctionClick);
            });

            scopedElements(scope, '.bls__product-compare-js').forEach(element => {
                if (initializedCompares.has(element) || !actionApi || typeof actionApi.handleCompareFunctionClick !== 'function') return;
                initializedCompares.add(element);
                element.addEventListener('click', actionApi.handleCompareFunctionClick);
            });

            this.initializeWishlistState(scopedElements(scope, '.bls__product-wishlist'));
            this.initializeCompareState(scopedElements(scope, '.bls__product-compare'));
        },

        initializeWishlistState: function(elements) {
            if (!elements.length || typeof localStorage === 'undefined') return;
            let wishlistItems = null;
            try {
                wishlistItems = JSON.parse(localStorage.getItem('bls__wishlist-items'));
            } catch (error) {
                wishlistItems = null;
            }
            const wishlistPage = document.querySelector('.bls__wishlist-page-section');
            elements.forEach(element => {
                const icon = element.querySelector('.bls__product-icon');
                const tooltip = element.querySelector('.bls_tooltip-content');
                if (!icon || !tooltip) return;
                if (!Array.isArray(wishlistItems)) return;
                const isSelected = wishlistItems.indexOf(element.dataset.productHandle) !== -1;
                icon.classList.toggle('active', isSelected);
                if (isSelected) {
                    tooltip.innerText = wishlistPage
                        ? window.stringsTemplate && window.stringsTemplate.messageRemoveWishlist
                        : element.dataset.action === 'remove'
                            ? element.dataset.removeWishlist
                            : element.dataset.proRemoveWishlist;
                } else {
                    tooltip.innerText = element.dataset.proAddWishlist;
                }
            });
            if (typeof BlsWishlistHeader !== 'undefined' && BlsWishlistHeader && typeof BlsWishlistHeader.init === 'function') {
                BlsWishlistHeader.init();
            }
        },

        initializeCompareState: function(elements) {
            if (!elements.length || typeof localStorage === 'undefined') return;
            let compareItems = null;
            try {
                compareItems = JSON.parse(localStorage.getItem('bls__compare-items'));
            } catch (error) {
                compareItems = null;
            }
            const comparePage = document.querySelector('.bls__compare-page-section');
            elements.forEach(element => {
                const icon = element.querySelector('.bls__product-icon');
                const tooltip = element.querySelector('.bls_tooltip-content');
                if (!icon || !tooltip) return;
                if (!Array.isArray(compareItems)) return;
                const isSelected = compareItems.indexOf(element.dataset.productHandle) !== -1;
                icon.classList.toggle('active', isSelected);
                tooltip.innerText = isSelected
                    ? comparePage
                        ? window.stringsTemplate && window.stringsTemplate.messageRemoveCompare
                        : element.dataset.proRemoveCompare
                    : element.dataset.proAddCompare;
            });
        },

        loadMoreProducts: function(target) {
            if (!target || target.classList.contains('loading')) return Promise.resolve(false);
            const nextUrl = target.dataset.nextUrl || target.getAttribute('href');
            const sectionId = target.dataset.sectionId;
            const currentPage = parseInt(target.dataset.currentPage || 1);
            const signal = sectionController ? sectionController.signal : undefined;
            const requestRoot = sectionRoot;
            const requestSectionGeneration = sectionGeneration;
            const appendedNodes = [];
            let didAppend = false;

            if (!nextUrl || !sectionId) {
                console.error('Collection pagination requires a next URL and section ID');
                return Promise.resolve(false);
            }

            this.toggleLoading(target, true);

            const separator = nextUrl.includes('?') ? '&' : '?';
            const fetchUrl = `${nextUrl}${separator}sections=${encodeURIComponent(sectionId)}`;

            return fetch(`${fetchUrl}`, { signal: signal })
                .then((response) => {
                    if (!response.ok) {
                        var error = new Error(response.status);
                        throw error;
                    }
                    return response.json();
                })
                .then(data => {
                    if (!currentSectionMatches(requestRoot, requestSectionGeneration)) return false;
                    const sectionHtml = data && data[sectionId];
                    if (typeof sectionHtml !== 'string' || !sectionHtml.trim()) {
                        throw new Error('Section HTML not found in response');
                    }

                    const parser = new DOMParser();
                    const productNodes = parser.parseFromString(sectionHtml, 'text/html');
                    const productNodesHtml = Array.from(productNodes.querySelectorAll('#bls__product-grid .bls__grid__item'));
                    if (
                        productNodesHtml.length === 0 ||
                        productNodesHtml.some(prodNode => !prodNode.querySelector('.bls__product-item'))
                    ) {
                        throw new Error('Invalid collection section HTML: complete product grid items not found');
                    }
                    const load_more = productNodes.querySelector('.collections-load-more');
                    let nextPageUrl = null;
                    if (load_more) {
                        const continuationUrl = load_more.dataset.nextUrl || load_more.getAttribute('href');
                        if (typeof continuationUrl !== 'string' || !continuationUrl.trim()) {
                            throw new Error('Invalid collection section HTML: continuation URL not found');
                        }
                        nextPageUrl = continuationUrl.trim();
                    }
                    const currentGrid = document.getElementById("bls__product-grid");
                    if (!currentGrid || !currentSectionMatches(requestRoot, requestSectionGeneration)) return false;
                    productNodesHtml.forEach(prodNode => {
                        currentGrid.appendChild(prodNode);
                        appendedNodes.push(prodNode);
                    });

                    // Update progress bar
                    const newLoadMoreAmount = productNodes.querySelector('.load-more-amount');
                    const newLoadMorePercent = productNodes.querySelector('.load-more-percent');
                    if (newLoadMoreAmount && document.querySelector(".load-more-amount")) {
                        document.querySelector(".load-more-amount").innerHTML = newLoadMoreAmount.textContent;
                    }
                    if (newLoadMorePercent && document.querySelector(".load-more-percent")) {
                        document.querySelector(".load-more-percent").style.width = newLoadMorePercent.style.width;
                    }

                    // Update load more button for next page
                    if (load_more) {
                        target.setAttribute("href", nextPageUrl);
                        target.dataset.nextUrl = nextPageUrl;
                    } else {
                        target.remove();
                    }

                    // Update URL hash to reflect current virtual page
                    const newPage = currentPage + 1;
                    const newHash = `#p=${newPage}`;
                    mutateCollectionHistory('replaceState', null, '', newHash);
                    target.dataset.currentPage = newPage;
                    didAppend = true;
                    return true;
                })
                .catch((error) => {
                    if (error.name !== 'AbortError') console.error('Load more error:', error);
                    return false;
                })
                .finally(() => {
                    if (currentSectionMatches(requestRoot, requestSectionGeneration)) {
                        this.toggleLoading(target, false);
                    }
                    if (didAppend && currentSectionMatches(requestRoot, requestSectionGeneration)) {
                        this.initializeProductIntegrations(appendedNodes);
                        this.activateLazyImages(requestRoot);
                    }
                });
        },

        toggleLoading: function(event, loading) {
            if (event) {
                if (loading) {
                  event.classList.add('start', 'loading');
                } else {
                  event.classList.add('finish');
                  setTimeout(function() {
                    event.classList.remove('start', 'loading', 'finish');
                  }, 500)
                }
            }
        },

        updateUrl: function(url, searchParams = false) {
            if (searchParams) {
                const urlUpdate = `${window.location.pathname}?${searchParams}`;
                mutateCollectionHistory('pushState', {url: urlUpdate}, '', urlUpdate);
            } else {
                mutateCollectionHistory('pushState', {url: url}, '', url);
            }
        },

        replayPagesFromHash: async function() {
            // Guard against concurrent replays
            if (isReplayInProgress) return false;

            // Parse hash to get target page (e.g. #p=3)
            const hash = window.location.hash;
            if (!hash || !hash.includes('p=')) return false;

            const targetPage = parseInt(hash.replace('#p=', '')) || 1;
            if (targetPage <= 1) return false;

            const loadMoreButton = document.querySelector(options.collectionsLoadMore);
            if (!loadMoreButton) return false;

            const sectionId = loadMoreButton.dataset.sectionId;
            if (!sectionId) return false;

            // Check if products for target page are already loaded (for bfcache scenarios)
            const currentProductCount = document.querySelectorAll('#bls__product-grid .bls__grid__item').length;
            const productsPerPage = parseInt(loadMoreButton.dataset.productsPerPage) || 20; // from Liquid paginate.page_size
            const expectedProducts = targetPage * productsPerPage;
            clearScrollRestoreTimers();

            // If products seem to be already there (bfcache), skip reload and just restore scroll
            if (currentProductCount >= expectedProducts * 0.8) {
                this.checkScrollRestore(100);
                return true;
            }

            // Set flag to prevent double replay
            isReplayInProgress = true;
            const controller = new AbortController();
            replayController = controller;
            const replay = {
                controller: controller,
                root: sectionRoot,
                sectionGeneration: sectionGeneration,
                button: loadMoreButton
            };
            const replaySignal = controller.signal;

            // Build URLs for pages 2..targetPage, preserving current filters/sort
            const currentParams = new URLSearchParams(window.location.search);
            const pagesToLoad = [];

            for (let page = 2; page <= targetPage; page++) {
                const params = new URLSearchParams(currentParams);
                params.set('page', page);
                pagesToLoad.push({
                    page: page,
                    url: `${window.location.pathname}?${params.toString()}`
                });
            }

            const appendedNodes = [];
            try {
                for (const pageData of pagesToLoad) {
                    try {
                        if (!currentReplayMatches(replay, true)) return false;
                        const separator = pageData.url.includes('?') ? '&' : '?';
                        const fetchUrl = `${pageData.url}${separator}sections=${encodeURIComponent(sectionId)}`;
                        const response = await fetch(fetchUrl, { signal: replaySignal });
                        if (!response.ok) throw new Error(response.status);
                        const data = await response.json();
                        if (!currentReplayMatches(replay, true)) return false;
                        const sectionHtml = data && data[sectionId];
                        if (typeof sectionHtml !== 'string' || !sectionHtml.trim()) {
                            throw new Error('Section HTML not found in response');
                        }

                        const parser = new DOMParser();
                        const productNodes = parser.parseFromString(sectionHtml, 'text/html');
                        const productNodesHtml = Array.from(productNodes.querySelectorAll('#bls__product-grid .bls__grid__item'));
                        if (
                            productNodesHtml.length === 0 ||
                            productNodesHtml.some(prodNode => !prodNode.querySelector('.bls__product-item'))
                        ) {
                            throw new Error('Invalid collection section HTML: complete product grid items not found');
                        }
                        const currentGrid = document.getElementById("bls__product-grid");
                        if (!currentGrid || !currentReplayMatches(replay, true)) return false;
                        productNodesHtml.forEach(prodNode => {
                            currentGrid.appendChild(prodNode);
                            appendedNodes.push(prodNode);
                        });

                        const newLoadMoreAmount = productNodes.querySelector('.load-more-amount');
                        const newLoadMorePercent = productNodes.querySelector('.load-more-percent');
                        const currentAmount = document.querySelector(".load-more-amount");
                        const currentPercent = document.querySelector(".load-more-percent");
                        if (newLoadMoreAmount && currentAmount && currentReplayMatches(replay, true)) {
                            currentAmount.innerHTML = newLoadMoreAmount.textContent;
                        }
                        if (newLoadMorePercent && currentPercent && currentReplayMatches(replay, true)) {
                            currentPercent.style.width = newLoadMorePercent.style.width;
                        }
                    } catch (error) {
                        if (error && typeof error === 'object') {
                            error.replayPage = pageData;
                            throw error;
                        }
                        const replayError = new Error(String(error));
                        replayError.replayPage = pageData;
                        throw replayError;
                    }
                }

                if (!currentReplayMatches(replay, true)) return false;
                await this.updateLoadMoreButton(loadMoreButton, targetPage, sectionId, replay);
                if (!currentReplayMatches(replay, false)) return false;
                this.initializeProductIntegrations(appendedNodes);
                this.activateLazyImages(replay.root);
                this.checkScrollRestore();
                return true;
            } catch (error) {
                if (error.name !== 'AbortError') {
                    if (error.replayPage && currentReplayMatches(replay, true)) {
                        loadMoreButton.setAttribute('href', error.replayPage.url);
                        loadMoreButton.dataset.nextUrl = error.replayPage.url;
                        loadMoreButton.dataset.currentPage = error.replayPage.page - 1;
                    }
                    console.error('Collection replay error:', error);
                }
                return false;
            } finally {
                if (replayController === controller) {
                    replayController = null;
                    isReplayInProgress = false;
                }
            }
        },

        updateLoadMoreButton: function(button, currentPage, sectionId, replay) {
            // Build URL for next page
            const currentParams = new URLSearchParams(window.location.search);
            const nextPage = currentPage + 1;
            const params = new URLSearchParams(currentParams);
            params.set('page', nextPage);
            const nextUrl = `${window.location.pathname}?${params.toString()}`;
            const separator = nextUrl.includes('?') ? '&' : '?';
            const checkUrl = `${nextUrl}${separator}sections=${encodeURIComponent(sectionId)}`;

            // Check if next page exists by fetching it
            return fetch(checkUrl, { signal: replay.controller.signal })
                .then(response => {
                    if (!response.ok) throw new Error(response.status);
                    return response.json();
                })
                .then(data => {
                    if (!currentReplayMatches(replay, true)) return false;
                    const sectionHtml = data[sectionId];
                    if (!sectionHtml) {
                        button.remove();
                        return true;
                    }

                    const parser = new DOMParser();
                    const productNodes = parser.parseFromString(sectionHtml, 'text/html');
                    const hasNextPage = productNodes.querySelector('.collections-load-more');

                    if (hasNextPage) {
                        button.setAttribute('href', nextUrl);
                        button.dataset.nextUrl = nextUrl;
                        button.dataset.currentPage = currentPage;
                    } else {
                        button.remove();
                    }
                    return true;
                })
                .catch((error) => {
                    if (error.name !== 'AbortError') {
                        if (currentReplayMatches(replay, true)) {
                            button.setAttribute('href', nextUrl);
                            button.dataset.nextUrl = nextUrl;
                            button.dataset.currentPage = currentPage;
                        }
                        console.error('Load more availability check error:', error);
                    }
                    return false;
                });
        },

        checkScrollRestore: function(replayDelay) {
            clearScrollRestoreTimers();
            const savedState = sessionStorage.getItem('collectionReturnState');
            if (!savedState || !sectionRoot || !sectionController) return;

            const locationSnapshot = normalizedLocation();
            const restore = {
                root: sectionRoot,
                sectionGeneration: sectionGeneration,
                controller: sectionController,
                locationEpoch: locationRestoreEpoch,
                url: locationSnapshot.url,
                hash: locationSnapshot.hash,
                savedState: savedState,
                state: null,
                savedUrl: '',
                savedHash: ''
            };
            try {
                restore.state = JSON.parse(savedState);
                if (
                    !restore.state ||
                    typeof restore.state !== 'object' ||
                    Array.isArray(restore.state) ||
                    !Object.prototype.hasOwnProperty.call(restore.state, 'collectionUrl') ||
                    typeof restore.state.collectionUrl !== 'string' ||
                    !Object.prototype.hasOwnProperty.call(restore.state, 'collectionHash') ||
                    typeof restore.state.collectionHash !== 'string' ||
                    !Object.prototype.hasOwnProperty.call(restore.state, 'scrollY') ||
                    typeof restore.state.scrollY !== 'number' ||
                    !Number.isFinite(restore.state.scrollY)
                ) {
                    releaseScrollRestore(restore);
                    return;
                }
                restore.savedUrl = normalizedCollectionUrl(restore.state.collectionUrl);
                restore.savedHash = restore.state.collectionHash;
                scrollRestoreChain = restore;
                if (!currentScrollRestoreMatches(restore)) {
                    releaseScrollRestore(restore);
                    return;
                }
            } catch (e) {
                console.error('Scroll restore error:', e);
                releaseScrollRestore(restore);
                return;
            }

            const scheduleScroll = () => {
                if (!currentScrollRestoreMatches(restore)) {
                    releaseScrollRestore(restore);
                    return;
                }
                scrollRestoreTimer = setTimeout(() => {
                    scrollRestoreTimer = null;
                    if (!currentScrollRestoreMatches(restore)) {
                        releaseScrollRestore(restore);
                        return;
                    }
                    if (restore.state.scrollY && currentScrollRestoreMatches(restore)) {
                        window.scrollTo(0, restore.state.scrollY);
                    }

                    // Or scroll to the product card if we have the handle
                    if (restore.state.productHandle && currentScrollRestoreMatches(restore)) {
                        const productCard = document.querySelector(`#bls__product-grid a[href*="/products/${restore.state.productHandle}"]`);
                        if (productCard && currentScrollRestoreMatches(restore)) {
                            hydrateViewportImage(productCard.querySelector('.bls__product-main-img img'));
                            productCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }

                    // Clean up only if this exact restore still owns the saved state.
                    if (currentScrollRestoreMatches(restore)) {
                        sessionStorage.removeItem('collectionReturnState');
                    }
                    releaseScrollRestore(restore);
                }, 300); // Increased delay for Safari back button
            };

            if (replayDelay) {
                if (!currentScrollRestoreMatches(restore)) {
                    releaseScrollRestore(restore);
                    return;
                }
                replayRestoreTimer = setTimeout(() => {
                    replayRestoreTimer = null;
                    scheduleScroll();
                }, replayDelay);
            } else {
                scheduleScroll();
            }
        }
    }
})();


BlsEventCollectionShopify.init();
